import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCourseById, updateCourse, getCourses, deleteCourse, deleteLecture, deleteUploadedFile, uploadFile, uploadCourseImage, UPLOAD_URL, getMediaUrl } from '../../services/api';


const ManageCourse = ({ courseId: propCourseId, onBack }) => {
  const navigate = useNavigate();
  const { courseId: urlCourseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('basic-info');
  const [courseId, setCourseId] = useState('');
  const [isDirty, setIsDirty] = useState(false);


  // State management
  const [courseData, setCourseData] = useState({
    id: null,
    title: '',
    instructor: '',
    category: '',
    course: '',
    image: '',
    price: 0,
    originalPrice: 0,
    discount: 0,
    rating: 0,
    ratingCount: 0,
    students: 0,
    duration: '',
    lectures: 0,
    level: '',
    difficulty: '',
    lastUpdated: '',
    articles: 0,
    resources: 0,
    recommended: false,
    hasHardcopyCertificate: false,
    certificateTitle: '',
    certificatePrice: 499,
    description: '',
    fullDescription: '',
    whatYouLearn: [],
    requirements: [],
    skills: [],
    curriculum: [],
    instructorDetails: {
      name: '',
      title: '',
      avatar: '',
      rating: 0,
      students: 0,
      courses: 0,
      bio: ''
    }
  });

  const [sections, setSections] = useState([]);
  const [draggedLecture, setDraggedLecture] = useState(null);
  const [draggedOverLecture, setDraggedOverLecture] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null);
  const [draggedOverSection, setDraggedOverSection] = useState(null);
  const [whatYouLearn, setWhatYouLearn] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [skills, setSkills] = useState([]);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [showLectureModal, setShowLectureModal] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
  const [editingLecture, setEditingLecture] = useState(null);
  const [lectureData, setLectureData] = useState({
    title: '',
    duration: '',
    videoUrl: '',
    type: 'video',
    preview: false
  });
  const [isUploading, setIsUploading] = useState(false);
  const [activeUploadsCount, setActiveUploadsCount] = useState(0);

  useEffect(() => {
    setIsUploading(activeUploadsCount > 0);
  }, [activeUploadsCount]);
  
  const [availableCourses, setAvailableCourses] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, type: 'danger', isAlert: false, confirmText: 'Confirm' });
  const [videoPreview, setVideoPreview] = useState(null);
  const [studyMaterials, setStudyMaterials] = useState([]);
  const [newMaterialTitle, setNewMaterialTitle] = useState('');
  const [newMaterialType, setNewMaterialType] = useState('pdf');
  const [materialUploading, setMaterialUploading] = useState(false);
  const [materialUploadProgress, setMaterialUploadProgress] = useState(0);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  const thumbnailInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const materialFileInputRef = useRef(null);
  const fullDescriptionRef = useRef(null);
  const originalVideoUrlRef = useRef('');  // Track original video URL for cleanup on re-upload


  // Set courseId from URL params or props
  useEffect(() => {
    const id = urlCourseId || propCourseId;
    if (id && id !== courseId) {
      setCourseId(id);
    }
  }, [urlCourseId, propCourseId, courseId]);

  // Handle browser back/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Simulated data fetch - Replace with actual API call
  useEffect(() => {
    if (courseId) {
      // Simulate API call
      setTimeout(() => {
        loadCourseData(courseId);
        setLoading(false);
      }, 500);
    }
  }, [courseId]);

  // Fetch available courses for copying
  useEffect(() => {
    const fetchAvailableCourses = async () => {
      try {
        const response = await getCourses(true);
        if (response.success) {
          setAvailableCourses(response.data.filter(c => c.id !== parseInt(courseId))); // Exclude current course
        }
      } catch (error) {
        console.error("Failed to fetch available courses:", error);
      }
    };
    if (courseId) {
      fetchAvailableCourses();
    }
  }, [courseId]);

  // Load course data from API
  const loadCourseData = async (id) => {
    try {
      const response = await getCourseById(id);
      if (response.success && response.data) {
        const course = response.data;
        setCourseData({
          ...course,
          department: course.department || '', // Map department to department for form compatibility
          hasHardcopyCertificate: course.hasHardcopyCertificate === true,
          certificateTitle: course.certificateTitle || '',
          certificatePrice: course.certificatePrice || 499,
          instructorDetails: {
            name: course.instructor_name || course.instructor || '',
            title: course.instructor_title || '',
            avatar: course.instructor_avatar || '',
            bio: course.instructor_bio || ''
          }
        });
        setSections((course.curriculum || []).map(section => ({
          ...section,
          lectures: (section.lectures || []).map(lecture => ({
            ...lecture,
            videoUrl: lecture.videoUrl || lecture.video_url || '',
            video_url: lecture.video_url || lecture.videoUrl || ''
          }))
        })));
        setWhatYouLearn(course.whatYouLearn || course.learning_points || []);
        setRequirements(course.requirements || []);
        setSkills(course.skills || []);
        setStudyMaterials(course.studyMaterials || course.study_materials || []);
        setThumbnailPreview(course.image || null);


        if (fullDescriptionRef.current) {
          fullDescriptionRef.current.innerHTML = course.full_description || '';
        }
      }
    } catch (error) {
      console.error("Failed to load course details:", error);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    setIsDirty(true);
    const { name, value, type, checked } = e.target;

    if (name.startsWith('instructor.')) {
      const field = name.split('.')[1];
      setCourseData({
        ...courseData,
        instructorDetails: {
          ...courseData.instructorDetails,
          [field]: type === 'checkbox' ? checked : value
        }
      });
    } else {
      setCourseData({
        ...courseData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Calculate discount percentage
  useEffect(() => {
    if (courseData.price && courseData.originalPrice) {
      const discount = Math.round(((courseData.originalPrice - courseData.price) / courseData.originalPrice) * 100);
      setCourseData(prev => ({ ...prev, discount }));
    }
  }, [courseData.price, courseData.originalPrice]);

  // Handle thumbnail upload
  const handleThumbnailUpload = async (e) => {
    setIsDirty(true);
    const file = e.target.files[0];
    if (file) {
      // Show immediate local preview
      const localPreviewUrl = URL.createObjectURL(file);
      setThumbnailPreview(localPreviewUrl);
      
      try {
        const result = await uploadCourseImage(file);
        if (result.success) {
          setCourseData(prev => ({ ...prev, image: result.url }));
          setThumbnailPreview(result.url);
          showToast('Thumbnail uploaded successfully!', 'success');
        } else {
          showToast('Failed to upload thumbnail: ' + result.message, 'error');
        }
      } catch (error) {
        console.error('Error uploading thumbnail:', error);
        showToast('Error uploading thumbnail file to server.', 'error');
      }
    }
  };

  // Handle video upload
  const handleVideoUpload = (e) => {
    setIsDirty(true);
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setVideoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove thumbnail
  const removeThumbnail = () => {
    setIsDirty(true);
    setThumbnailPreview(null);
    setCourseData({ ...courseData, image: '' });
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  // Remove video
  const removeVideo = () => {
    setIsDirty(true);
    setVideoPreview(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  // Study Materials Handlers
  const handleMaterialFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    let detectedType = newMaterialType;
    if (file.type.startsWith('image/')) {
      detectedType = 'image';
      setNewMaterialType('image');
    } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      detectedType = 'pdf';
      setNewMaterialType('pdf');
    }

    setMaterialUploading(true);
    setMaterialUploadProgress(0);
    setActiveUploadsCount(prev => prev + 1);

    try {
      const result = await uploadFile(file, (progressInfo) => {
        if (progressInfo && typeof progressInfo.percentage === 'number') {
          setMaterialUploadProgress(progressInfo.percentage);
        }
      });

      if (result && result.url) {
        const materialTitle = newMaterialTitle.trim() || file.name.replace(/\.[^/.]+$/, "");
        const newMat = {
          title: materialTitle,
          fileUrl: result.url,
          fileType: detectedType
        };
        setStudyMaterials(prev => [...prev, newMat]);
        setIsDirty(true);
        setNewMaterialTitle('');
        showToast('Study material uploaded successfully!', 'success');
      } else {
        showToast('Failed to upload material: ' + (result?.message || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Material upload error:', err);
      showToast('Upload failed: ' + (err.message || 'Error uploading file'), 'error');
    } finally {
      setMaterialUploading(false);
      setMaterialUploadProgress(0);
      setActiveUploadsCount(prev => Math.max(0, prev - 1));
      if (materialFileInputRef.current) materialFileInputRef.current.value = '';
    }
  };

  const handleRemoveMaterial = (index) => {
    setIsDirty(true);
    setStudyMaterials(prev => prev.filter((_, i) => i !== index));
    showToast('Study material removed', 'info');
  };


  // Drag and drop for sections
  const handleSectionDragStart = (e, index) => {
    if (e.target.closest('[draggable="true"]') && e.target.closest('[draggable="true"]') !== e.currentTarget) {
      return;
    }
    if (e.target.closest('.section-drag-handle')) {
      setDraggedSection(index);
      e.dataTransfer.effectAllowed = 'move';
    } else {
      e.preventDefault();
    }
  };

  const handleSectionDragOver = (e, index) => {
    e.preventDefault();
    if (draggedSection !== null && draggedOverSection !== index) {
      setDraggedOverSection(index);
    }
  };

  const handleSectionDrop = (e, targetIndex) => {
    e.preventDefault();
    setDraggedOverSection(null);
    if (draggedSection === null || draggedSection === targetIndex) {
      setDraggedSection(null);
      return;
    }
    setIsDirty(true);
    const updatedSections = [...sections];
    const [removed] = updatedSections.splice(draggedSection, 1);
    updatedSections.splice(targetIndex, 0, removed);
    setSections(updatedSections);
    setDraggedSection(null);
  };

  const handleSectionDragEnd = () => {
    setDraggedSection(null);
    setDraggedOverSection(null);
  };

  // Drag and drop for lectures
  const handleLectureDragStart = (e, sectionIndex, lectureIndex) => {
    e.stopPropagation();
    setDraggedLecture({ sectionIndex, lectureIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleLectureDragOver = (e, sectionIndex, lectureIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedLecture !== null && 
        (!draggedOverLecture || draggedOverLecture.sectionIndex !== sectionIndex || draggedOverLecture.lectureIndex !== lectureIndex)) {
      setDraggedOverLecture({ sectionIndex, lectureIndex });
    }
  };

  const handleLectureDrop = (e, targetSectionIndex, targetLectureIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverLecture(null);
    if (!draggedLecture) return;
    const { sectionIndex: sourceSectionIndex, lectureIndex: sourceLectureIndex } = draggedLecture;

    if (sourceSectionIndex === targetSectionIndex) {
      if (sourceLectureIndex === targetLectureIndex) {
        setDraggedLecture(null);
        return;
      }
      setIsDirty(true);
      const updatedSections = [...sections];
      const section = { ...updatedSections[sourceSectionIndex] };
      const lectures = [...(section.lectures || [])];
      
      const [removed] = lectures.splice(sourceLectureIndex, 1);
      lectures.splice(targetLectureIndex, 0, removed);
      
      section.lectures = lectures;
      updatedSections[sourceSectionIndex] = section;
      setSections(updatedSections);
    } else {
      setIsDirty(true);
      const updatedSections = [...sections];
      const sourceSection = { ...updatedSections[sourceSectionIndex] };
      const targetSection = { ...updatedSections[targetSectionIndex] };
      
      const sourceLectures = [...(sourceSection.lectures || [])];
      const targetLectures = [...(targetSection.lectures || [])];
      
      const [removed] = sourceLectures.splice(sourceLectureIndex, 1);
      targetLectures.splice(targetLectureIndex, 0, removed);
      
      sourceSection.lectures = sourceLectures;
      targetSection.lectures = targetLectures;
      
      updatedSections[sourceSectionIndex] = sourceSection;
      updatedSections[targetSectionIndex] = targetSection;
      setSections(updatedSections);
    }
    setDraggedLecture(null);
  };

  const handleLectureDragEnd = (e) => {
    e.stopPropagation();
    setDraggedLecture(null);
    setDraggedOverLecture(null);
  };

  // Curriculum functions
  const addSection = () => {
    setIsDirty(true);
    const newSection = {
      title: 'New Section',
      duration: '0h 0m',
      lectures: []
    };
    setSections(prev => [...prev, newSection]);
  };

  const removeSection = (index) => {
    setConfirmModal({
      show: true,
      title: 'Delete Section?',
      message: 'Are you sure you want to delete this section and all its lectures?',
      type: 'danger',
      onConfirm: () => {
        setIsDirty(true);
        setSections(sections.filter((_, i) => i !== index));
        setConfirmModal({ ...confirmModal, show: false });
      }
    });
  };

  const updateSectionTitle = (index, title) => {
    setIsDirty(true);
    const updatedSections = [...sections];
    updatedSections[index].title = title;
    setSections(updatedSections);
  };

  const openLectureModal = (sectionIndex, lecture = null) => {
    setCurrentSectionIndex(sectionIndex);
    if (lecture) {
      setEditingLecture(lecture);
      setLectureData({
        ...lecture,
        tempId: lecture.tempId || lecture.id || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      });
      originalVideoUrlRef.current = lecture.videoUrl || lecture.video_url || '';
    } else {
      setEditingLecture(null);
      setLectureData({
        title: '',
        duration: '',
        videoUrl: '',
        type: 'video',
        preview: false,
        tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      });
      originalVideoUrlRef.current = '';
    }
    setShowLectureModal(true);
  };

  const closeLectureModal = () => {
    setShowLectureModal(false);
    setEditingLecture(null);
    setLectureData({ title: '', duration: '', videoUrl: '', type: 'video', preview: false });
    setCurrentSectionIndex(null);
  };

  const startBackgroundUpload = (sectionIndex, lectureTempId, file) => {
    if (!file) return;

    setActiveUploadsCount(prev => prev + 1);

    uploadFile(file, (percent, info) => {
      setSections(prevSections => {
        return prevSections.map((sec, sIdx) => {
          if (sIdx !== sectionIndex) return sec;
          const updatedLectures = (sec.lectures || []).map(lec => {
            if (lec.tempId === lectureTempId || (lec.id && lec.id === lectureTempId)) {
              return {
                ...lec,
                uploadProgress: percent,
                uploadInfo: info
              };
            }
            return lec;
          });
          return { ...sec, lectures: updatedLectures };
        });
      });
    })
    .then(result => {
      setSections(prevSections => {
        return prevSections.map((sec, sIdx) => {
          if (sIdx !== sectionIndex) return sec;
          const updatedLectures = (sec.lectures || []).map(lec => {
            if (lec.tempId === lectureTempId || (lec.id && lec.id === lectureTempId)) {
              return {
                ...lec,
                videoUrl: result.url,
                video_url: result.url,
                uploadProgress: null,
                uploadInfo: null
              };
            }
            return lec;
          });
          return { ...sec, lectures: updatedLectures };
        });
      });
      setActiveUploadsCount(prev => Math.max(0, prev - 1));
      showToast('Video uploaded successfully!', 'success');
    })
    .catch(error => {
      console.error('Background upload error:', error);
      showToast(`Upload failed: ${error.message || 'Unknown error'}`, 'error');
      setSections(prevSections => {
        return prevSections.map((sec, sIdx) => {
          if (sIdx !== sectionIndex) return sec;
          const updatedLectures = (sec.lectures || []).map(lec => {
            if (lec.tempId === lectureTempId || (lec.id && lec.id === lectureTempId)) {
              return {
                ...lec,
                videoUrl: lec.originalVideoUrl || '',
                video_url: lec.originalVideoUrl || '',
                uploadProgress: null,
                uploadInfo: null
              };
            }
            return lec;
          });
          return { ...sec, lectures: updatedLectures };
        });
      });
      setActiveUploadsCount(prev => Math.max(0, prev - 1));
    });
  };

  const saveLecture = () => {
    if (!lectureData.title || currentSectionIndex === null) return;
    setIsDirty(true);

    const file = lectureData.fileToUpload;
    const currentTempId = lectureData.tempId;

    // Create a copy of lectureData without the file object to store in state
    const cleanLecture = { ...lectureData };
    delete cleanLecture.fileToUpload;

    // If uploading a new file, record the original URL to revert on failure
    if (file) {
      cleanLecture.originalVideoUrl = cleanLecture.videoUrl || cleanLecture.video_url || '';
      cleanLecture.videoUrl = 'Uploading...';
      cleanLecture.video_url = 'Uploading...';
      cleanLecture.uploadProgress = 0;
    }

    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[currentSectionIndex] };
      const lectures = [...(section.lectures || [])];

      if (editingLecture) {
        const idx = lectures.findIndex(l => l.tempId === cleanLecture.tempId || (l.id && l.id === cleanLecture.id));
        if (idx !== -1) {
          lectures[idx] = { ...lectures[idx], ...cleanLecture, video_url: cleanLecture.videoUrl || '' };
        } else {
          lectures.push({ ...cleanLecture, video_url: cleanLecture.videoUrl || '' });
        }
      } else {
        lectures.push({ ...cleanLecture, video_url: cleanLecture.videoUrl || '' });
      }

      section.lectures = lectures;
      newSections[currentSectionIndex] = section;
      return newSections;
    });

    closeLectureModal();

    // Trigger background upload if file exists
    if (file) {
      startBackgroundUpload(currentSectionIndex, currentTempId, file);
    }
  };

  const removeLecture = (sectionIndex, lectureIndex) => {
    const lecture = sections[sectionIndex].lectures[lectureIndex];
    
    setConfirmModal({
      show: true,
      title: 'Delete Lecture?',
      message: `Are you sure you want to delete "${lecture.title}"? This will also permanently delete the video file from the server.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          // If lecture has a DB id, delete from backend (DB record + video file)
          if (lecture.id) {
            const result = await deleteLecture(lecture.id);
            if (!result.success) {
              throw new Error(result.message || 'Failed to delete lecture');
            }
          } else {
            // Lecture has no DB id (unsaved) - still delete the video file from server
            const videoUrl = lecture.videoUrl || lecture.video_url;
            if (videoUrl) {
              await deleteUploadedFile(videoUrl).catch(err => 
                console.warn('Could not delete video file:', err)
              );
            }
          }

          showToast('Lecture and video deleted successfully!', 'success');

          // Remove from local state
          setIsDirty(true);
          const updatedSections = [...sections];
          updatedSections[sectionIndex].lectures.splice(lectureIndex, 1);
          setSections(updatedSections);
          setConfirmModal(prev => ({ ...prev, show: false }));
        } catch (error) {
          console.error('Error deleting lecture:', error);
          showToast('Error deleting lecture: ' + error.message, 'error');
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  // List management functions
  const addWhatYouLearn = (value) => {
    if (value.trim()) {
      setIsDirty(true);
      setWhatYouLearn(prev => [...prev, value.trim()]);
    }
  };

  const removeWhatYouLearn = (index) => {
    setIsDirty(true);
    setWhatYouLearn(prev => prev.filter((_, i) => i !== index));
  };

  const addRequirement = (value) => {
    if (value.trim()) {
      setIsDirty(true);
      setRequirements(prev => [...prev, value.trim()]);
    }
  };

  const removeRequirement = (index) => {
    setIsDirty(true);
    setRequirements(prev => prev.filter((_, i) => i !== index));
  };

  const addSkill = (value) => {
    if (value.trim()) {
      setIsDirty(true);
      setSkills(prev => [...prev, value.trim()]);
    }
  };

  const removeSkill = (index) => {
    setIsDirty(true);
    setSkills(prev => prev.filter((_, i) => i !== index));
  };

  // Action handlers
  const handleSave = async (publishStatus = undefined) => {
    // Block save while a video is uploading
    if (isUploading) {
      showToast('Please wait for the video upload to finish before saving.', 'error');
      return;
    }

    try {
      // Auto-calculate lectures and duration from curriculum
      let totalLectures = 0;
      let totalMinutes = 0;

      sections.forEach(section => {
        if (section.lectures) {
          totalLectures += section.lectures.length;
          section.lectures.forEach(lecture => {
            if (lecture.duration) {
              const str = lecture.duration.toString().trim().toLowerCase();
              let mins = 0;

              if (str.includes(':')) {
                const parts = str.split(':');
                if (parts.length === 2) {
                  // MM:SS format
                  const m = parseInt(parts[0], 10);
                  const s = parseInt(parts[1], 10);
                  if (!isNaN(m)) mins += m + (isNaN(s) ? 0 : s / 60);
                } else if (parts.length === 3) {
                  // HH:MM:SS format
                  const h = parseInt(parts[0], 10);
                  const m = parseInt(parts[1], 10);
                  const s = parseInt(parts[2], 10);
                  if (!isNaN(h)) mins += h * 60;
                  if (!isNaN(m)) mins += m;
                  if (!isNaN(s)) mins += s / 60;
                }
              } else {
                const hoursMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hour|hours)/);
                if (hoursMatch) {
                  mins += parseFloat(hoursMatch[1]) * 60;
                }

                const minsMatch = str.match(/(\d+)\s*(?:m|min|mins|minute|minutes)/);
                if (minsMatch) {
                  mins += parseInt(minsMatch[1], 10);
                }

                if (!hoursMatch && !minsMatch) {
                  const justNum = str.match(/^(\d+)$/);
                  if (justNum) {
                    mins += parseInt(justNum[1], 10);
                  }
                }
              }

              totalMinutes += mins;
            }
          });
        }
      });

      const hours = Math.floor(totalMinutes / 60);
      const remainingMins = Math.round(totalMinutes % 60);
      let durationStr = "0 hours";

      if (hours > 0 && remainingMins > 0) {
        durationStr = `${hours + (remainingMins / 60)} hours`;
        // clean up decimal
        if (durationStr.includes('.')) {
          durationStr = parseFloat(hours + (remainingMins / 60)).toFixed(1) + " hours";
        }
      } else if (hours > 0) {
        durationStr = `${hours} hours`;
      } else if (remainingMins > 0) {
        durationStr = `${remainingMins} mins`;
      }

      // Sanitize lecture videoUrls - clean up any "Uploading..." values and sync both keys
      const sanitizedSections = sections.map(section => ({
        ...section,
        lectures: (section.lectures || []).map(lecture => {
          const url = (lecture.videoUrl === 'Uploading...' || lecture.video_url === 'Uploading...') 
            ? '' 
            : (lecture.videoUrl || lecture.video_url || '');
          return {
            ...lecture,
            videoUrl: url,
            video_url: url
          };
        })
      }));

      const updatedCourse = {
        ...courseData,
        instructor: courseData.instructorDetails?.name || courseData.instructor,
        lectures: totalLectures,
        duration: durationStr,
        curriculum: sanitizedSections,
        whatYouLearn,
        requirements,
        skills,
        studyMaterials,
        fullDescription: fullDescriptionRef.current?.innerHTML || ''
      };


      if (typeof publishStatus === 'boolean') {
        updatedCourse.isPublished = publishStatus;
      }

      const result = await updateCourse(courseId, updatedCourse);

      if (result.success) {
        setIsDirty(false);
        showToast('Course saved successfully!', 'success');
        // Reload course data to get fresh DB IDs for lectures
        await loadCourseData(courseId);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error saving course:', error);
      showToast('Error saving course: ' + error.message, 'error');
    }
  };

  const handlePreview = () => {
    const previewData = {
      ...courseData,
      curriculum: sections,
      whatYouLearn,
      requirements,
      skills,
      fullDescription: fullDescriptionRef.current?.innerHTML || ''
    };

    // Open preview in new window
    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Course Preview - ${courseData.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 1200px; margin: 0 auto; }
            h1 { color: #333; }
            .section { margin: 20px 0; padding: 20px; background: #f5f5f5; border-radius: 8px; }
          </style>
        </head>
        <body>
          <h1>${courseData.title}</h1>
          <div class="section">
            <h2>Preview</h2>
            <pre>${JSON.stringify(previewData, null, 2)}</pre>
          </div>
        </body>
      </html>
    `);
  };

  const handlePublish = async () => {
    setConfirmModal({
      show: true,
      title: 'Publish Course?',
      message: 'Are you sure you want to publish this course? It will be visible to all students.',
      type: 'success',
      onConfirm: async () => {
        try {
          await handleSave(true);
          showToast('Course published successfully!', 'success');
          setConfirmModal({ ...confirmModal, show: false });
        } catch (error) {
          console.error('Error publishing course:', error);
          showToast('Error publishing course. Please try again.', 'error');
        }
      }
    });
  };

  const handleUnpublish = async () => {
    setConfirmModal({
      show: true,
      title: 'Unpublish Course?',
      message: 'Are you sure you want to unpublish this course? It will be hidden from students and the front page.',
      type: 'warning',
      onConfirm: async () => {
        try {
          await handleSave(false);
          showToast('Course unpublished successfully!', 'success');
          setConfirmModal({ ...confirmModal, show: false });
        } catch (error) {
          console.error('Error unpublishing course:', error);
          showToast('Error unpublishing course. Please try again.', 'error');
        }
      }
    });
  };

  const handleDelete = async () => {
    setConfirmModal({
      show: true,
      title: 'Permanently Delete?',
      message: '⚠️ FINAL WARNING: This will permanently delete all course data. This action cannot be undone. Continue?',
      type: 'danger',
      onConfirm: async () => {
        try {
          const result = await deleteCourse(courseId);
          if (result.success) {
            showToast('Course deleted successfully!', 'success');
            setConfirmModal({ ...confirmModal, show: false });
            setTimeout(() => {
              window.location.href = '/admin/admindashboard';
            }, 1000);
          } else {
            throw new Error(result.message);
          }
        } catch (error) {
          console.error('Error deleting course:', error);
          showToast('Error deleting course: ' + error.message, 'error');
        }
      }
    });
  };

  const handleBack = () => {
    if (isDirty) {
      setConfirmModal({
        show: true,
        title: 'Unsaved Changes',
        message: 'Please save your changes before going back.',
        type: 'danger',
        isAlert: true,
        confirmText: 'OK',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
      });
    } else {
      window.location.href = "/admin/admindashboard";
    }
  };

  // Rich text editor functions
  const execCommand = (command) => {
    setIsDirty(true);
    document.execCommand(command, false, null);
  };

  // Navigation sections
  const navSections = [
    { id: 'basic-info', icon: 'fa-info-circle', label: 'Basic Information' },
    { id: 'pricing', icon: 'fa-dollar-sign', label: 'Pricing' },
    { id: 'media', icon: 'fa-image', label: 'Media & Thumbnail' },
    { id: 'curriculum', icon: 'fa-list', label: 'Curriculum' },
    { id: 'study-materials', icon: 'fa-folder-open', label: 'Study Materials' },
    { id: 'requirements', icon: 'fa-clipboard-list', label: 'Requirements' },
    { id: 'objectives', icon: 'fa-bullseye', label: 'What You\'ll Learn' },
    { id: 'instructor', icon: 'fa-[#00b26f] fa-user-tie', label: 'Instructor Info' },
  ];


  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6f8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00b26f]"></div>
          <p className="mt-4 text-[#666]">Loading course data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      {/* Header */}
      <header className="bg-white px-4 md:px-8 py-3 md:py-6 shadow-md sticky top-0 z-[100]">
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-3 md:gap-6 w-full sm:w-auto">
            <button
              onClick={handleBack}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:px-5 md:py-2.5 border-2 border-[#e1e5e9] rounded-lg font-semibold text-[#333] hover:bg-[#00b26f] hover:text-white hover:border-[#00b26f] transition-all duration-300 text-sm md:text-base whitespace-nowrap"
            >
              <i className="fa-solid fa-arrow-left"></i>
              <span className="hidden md:inline ml-2">Back</span>
            </button>
            <div className="overflow-hidden">
              <h1 className="text-lg md:text-2xl font-bold text-[#333] truncate">Manage Course</h1>
              <p className="text-[10px] md:text-sm text-[#666] font-medium">ID: #{courseId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
            <div className="flex gap-2">
              <button
                onClick={handlePreview}
                className="flex items-center justify-center w-10 h-10 md:w-auto md:px-4 md:py-2.5 bg-[#666] text-white rounded-lg font-semibold hover:bg-[#555] transition-all duration-300"
                title="Preview"
              >
                <i className="fa-solid fa-eye"></i>
                <span className="hidden xl:inline ml-2">Preview</span>
              </button>
              {courseData.isPublished ? (
                <button
                  onClick={handleUnpublish}
                  className="flex items-center justify-center w-10 h-10 md:w-auto md:px-4 md:py-2.5 bg-[#ffc107] text-white rounded-lg font-semibold hover:bg-[#e0a800] transition-all duration-300"
                  title="Unpublish"
                >
                  <i className="fa-solid fa-ban"></i>
                  <span className="hidden xl:inline ml-2">Unpublish</span>
                </button>
              ) : (
                <button
                  onClick={handlePublish}
                  className="flex items-center justify-center w-10 h-10 md:w-auto md:px-4 md:py-2.5 bg-[#28a745] text-white rounded-lg font-semibold hover:bg-[#218838] transition-all duration-300"
                  title="Publish"
                >
                  <i className="fa-solid fa-check"></i>
                  <span className="hidden xl:inline ml-2">Publish</span>
                </button>
              )}
            </div>
            <button
              onClick={() => handleSave()}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00b26f] text-white rounded-lg font-bold hover:bg-[#008f5a] hover:-translate-y-0.5 transition-all duration-300 shadow-md text-sm md:text-base whitespace-nowrap"
            >
              <i className="fa-solid fa-save"></i>
              <span>Save <span className="hidden sm:inline">Changes</span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-8 flex flex-col lg:flex-row gap-6 md:gap-8">
        {/* Sidebar Navigation */}
        <aside className="w-full lg:w-[280px] lg:sticky lg:top-[100px] h-fit z-40">
          <nav className="bg-white rounded-xl p-2 md:p-4 shadow-md flex lg:flex-col overflow-x-auto lg:overflow-x-visible gap-1 md:gap-2 no-scrollbar">
            {navSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex-none lg:w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-all duration-300 font-medium text-left text-sm md:text-base whitespace-nowrap ${activeSection === section.id
                  ? 'bg-[#00b26f] text-white shadow-md'
                  : 'text-[#333] hover:bg-[#f4f6f8]'
                  }`}
              >
                <i className={`fa-solid ${section.icon} w-4 md:w-5 text-center`}></i>
                {section.label}
              </button>
            ))}

            {/* Danger Zone */}
            <div className="flex-none lg:w-full mt-1 md:mt-2 lg:mt-6 pt-1 md:pt-2 lg:pt-6 border-l-2 lg:border-l-0 lg:border-t-2 border-[#e1e5e9]">
              <button
                onClick={handleDelete}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 font-medium text-left text-[#dc3545] hover:bg-[#dc3545] hover:text-white whitespace-nowrap"
              >
                <i className="fa-solid fa-trash w-5 text-center"></i>
                Delete <span className="lg:hidden xl:inline">Course</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Form */}
        <main className="flex-1">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Basic Information Section */}
            {activeSection === 'basic-info' && (
              <section className="bg-white rounded-xl p-5 md:p-8 mb-6 md:mb-8 shadow-md animate-fadeIn">
                <div className="mb-6 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-xl md:text-2xl font-bold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-info-circle text-[#00b26f]"></i> Basic Information
                  </h2>
                  <p className="text-[#666] text-[10px] md:text-sm mt-1">Provide the essential details about your course</p>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-6">
                  <div className="col-span-1">
                    <label className="block font-semibold mb-2 text-[#333] text-sm">
                      Course Title <span className="text-[#dc3545]">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={courseData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., Complete Web Development Bootcamp"
                      required
                    />
                    <small className="text-[#666] text-[0.85rem] mt-1 block">
                      A clear, descriptive title that tells students what they'll learn
                    </small>
                  </div>

                  <div className="col-span-1">
                    <label className="block font-semibold mb-2 text-[#333] text-sm">
                      Course Description
                    </label>
                    <textarea
                      name="description"
                      value={courseData.description}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="Brief description of the course"
                      rows="3"
                    />
                    <small className="text-[#666] text-[0.85rem] mt-1 block">
                      A short summary that appears beneath the course title
                    </small>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6">
                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Current Students</label>
                    <input
                      type="number"
                      name="students"
                      value={courseData.students}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., 645891"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Course Rating</label>
                    <input
                      type="number"
                      name="rating"
                      value={courseData.rating}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., 4.6"
                      min="0"
                      max="5"
                      step="0.1"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Number of Ratings</label>
                    <input
                      type="number"
                      name="ratingCount"
                      value={courseData.ratingCount}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., 98547"
                      min="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Last Updated</label>
                    <input
                      type="text"
                      name="lastUpdated"
                      value={courseData.lastUpdated}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., 10/2024"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Recommended Course</label>
                    <div className="flex items-center gap-4 h-[54px]">
                      <label className="relative inline-block w-[50px] h-[26px] cursor-pointer">
                        <input
                          type="checkbox"
                          name="recommended"
                          checked={courseData.recommended}
                          onChange={handleInputChange}
                          className="hidden"
                        />
                        <span className={`absolute inset-0 rounded-full transition-colors duration-300 ${courseData.recommended ? 'bg-[#00b26f]' : 'bg-[#e1e5e9]'}`}>
                          <span className={`absolute top-[3px] left-[3px] w-5 h-5 bg-white rounded-full transition-transform duration-300 ${courseData.recommended ? 'translate-x-6' : ''}`}></span>
                        </span>
                      </label>
                      <span className="text-sm text-[#333]">Feature this course</span>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Pricing Section */}
            {activeSection === 'pricing' && (
              <section className="bg-white rounded-xl p-5 md:p-8 mb-6 md:mb-8 shadow-md animate-fadeIn">
                <div className="mb-6 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-xl md:text-2xl font-bold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-dollar-sign text-[#00b26f]"></i> Pricing
                  </h2>
                  <p className="text-[#666] text-[10px] md:text-sm mt-1">Set your course pricing and discount offers</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">
                      Current Price (₹) <span className="text-[#dc3545]">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={courseData.price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., 449"
                      required
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Original Price (₹)</label>
                    <input
                      type="number"
                      name="originalPrice"
                      value={courseData.originalPrice}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., 3499"
                    />
                  </div>
                </div>

                {/* Hardcopy Certificate Configuration Box */}
                <div className="bg-emerald-50/70 border border-emerald-200 p-5 md:p-6 rounded-xl mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                        <i className="fa-solid fa-award text-emerald-600"></i> Hardcopy Certificate Option
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Offer physical hardcopy certificate options for this course on the student Course Overview page.
                      </p>
                    </div>

                    <label className="relative inline-block w-12 h-6 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        name="hasHardcopyCertificate"
                        checked={courseData.hasHardcopyCertificate}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <span className={`absolute inset-0 rounded-full transition-colors duration-300 ${courseData.hasHardcopyCertificate ? 'bg-[#00b26f]' : 'bg-gray-300'}`}>
                        <span className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform duration-300 ${courseData.hasHardcopyCertificate ? 'translate-x-6' : ''}`}></span>
                      </span>
                    </label>
                  </div>

                  {courseData.hasHardcopyCertificate && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-emerald-200/60">
                      <div className="md:col-span-2">
                        <label className="block font-semibold mb-1 text-gray-800 text-xs">
                          Certificate Option Title / Wording *
                        </label>
                        <input
                          type="text"
                          name="certificateTitle"
                          value={courseData.certificateTitle || ''}
                          onChange={handleInputChange}
                          className="w-full px-3.5 py-2.5 bg-white border border-emerald-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="e.g. Apply for MERN Stack Assessment - Get Your Hardcopy Certificate"
                        />
                      </div>

                      <div>
                        <label className="block font-semibold mb-1 text-gray-800 text-xs">
                          Certificate Price (₹) *
                        </label>
                        <input
                          type="number"
                          name="certificatePrice"
                          value={courseData.certificatePrice || 499}
                          onChange={handleInputChange}
                          className="w-full px-3.5 py-2.5 bg-white border border-emerald-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold"
                          placeholder="499"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-[#f4f6f8] p-5 md:p-8 rounded-xl mt-6 md:mt-8">
                  <h3 className="mb-3 md:mb-4 text-[#333] font-bold text-sm md:text-base">Pricing Preview</h3>
                  <div className="flex flex-wrap items-center gap-3 md:gap-4">
                    <span className="text-2xl md:text-4xl font-bold text-[#333]">
                      ₹{parseInt(courseData.price || 0).toLocaleString()}
                    </span>
                    {courseData.originalPrice && courseData.originalPrice > courseData.price && (
                      <div className="flex items-center gap-2">
                        <span className="text-base md:text-xl text-[#666] line-through">
                          ₹{parseInt(courseData.originalPrice || 0).toLocaleString()}
                        </span>
                        <span className="bg-[#28a745] text-white px-2 md:px-4 py-1 md:py-2 rounded-md font-bold text-xs md:text-sm">
                          {courseData.discount}% OFF
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-[#666] mt-3 text-xs md:text-sm font-medium">
                    Students will save <span className="text-[#00b26f] font-bold">₹{parseInt((courseData.originalPrice || 0) - (courseData.price || 0)).toLocaleString()}</span>
                  </p>
                </div>
              </section>
            )}

            {/* Media & Thumbnail Section */}
            {activeSection === 'media' && (
              <section className="bg-white rounded-xl p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-8 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-2xl font-semibold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-image"></i> Media & Thumbnail
                  </h2>
                  <p className="text-[#666] text-sm mt-2">Upload course thumbnail and promotional video</p>
                </div>

                <div className="grid grid-cols-1 gap-8">
                  {/* Thumbnail Upload */}
                  <div className="bg-[#f4f6f8] p-6 rounded-xl">
                    <h3 className="mb-4 text-[#333] font-semibold">Course Thumbnail</h3>
                    {!thumbnailPreview ? (
                      <div
                        onClick={() => thumbnailInputRef.current?.click()}
                        className="border-2 border-dashed border-[#e1e5e9] rounded-xl p-12 text-center cursor-pointer transition-all duration-300 hover:border-[#00b26f] hover:bg-[rgba(0,178,111,0.05)]"
                      >
                        <i className="fa-solid fa-cloud-upload-alt text-5xl text-[#00b26f] mb-4 block"></i>
                        <p className="font-semibold mb-2 text-[#333]">Click to upload or drag and drop</p>
                        <small className="text-[#666]">PNG, JPG or JPEG (max. 2MB)</small>
                        <input
                          ref={thumbnailInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailUpload}
                          className="hidden"
                        />
                      </div>
                    ) : (
                      <div className="relative rounded-xl overflow-hidden flex justify-center">
                        <img 
                          src={getMediaUrl(thumbnailPreview)} 
                          alt="Thumbnail" 
                          className="w-48 h-32 object-cover rounded-xl" 
                        />
                        <button
                          type="button"
                          onClick={removeThumbnail}
                          className="absolute top-2 right-2 w-8 h-8 bg-[#dc3545] text-white rounded-full flex items-center justify-center hover:bg-[#c82333] hover:scale-110 transition-all duration-300"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </div>
                    )}
                    <div className="mt-4">
                      <input
                        type="text"
                        name="image"
                        value={courseData.image}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (e.target.value) setThumbnailPreview(e.target.value);
                        }}
                        className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                        placeholder="Or paste image URL"
                      />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Curriculum Section */}
            {activeSection === 'curriculum' && (
              <section className="bg-white rounded-xl p-5 md:p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-6 md:mb-8 pb-4 border-b-2 border-[#e1e5e9] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-[#333] flex items-center gap-2">
                      <i className="fa-solid fa-list text-[#00b26f]"></i> Course Curriculum
                    </h2>
                    <p className="text-[#666] text-[10px] md:text-sm mt-1 font-medium">
                      Manage sections and lectures ({sections.length} sections)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addSection}
                    className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-[#00b26f] text-white rounded-xl font-bold hover:bg-[#008f5a] transition-all duration-300 text-sm md:text-base w-full sm:w-auto justify-center shadow-lg hover:-translate-y-0.5 active:scale-95"
                  >
                    <i className="fa-solid fa-plus"></i> Add New Section
                  </button>
                </div>

                <div className="flex flex-col gap-6">
                  {sections.map((section, sectionIndex) => {
                    const isSecDragged = sectionIndex === draggedSection;
                    const isSecDraggedOver = sectionIndex === draggedOverSection && draggedSection !== sectionIndex;
                    return (
                      <div 
                        key={sectionIndex} 
                        className={`bg-[#f4f6f8] rounded-xl p-4 md:p-6 border-2 transition-all duration-250 ${
                          isSecDragged ? 'opacity-30 border-dashed border-[#00b26f] bg-emerald-50/10' : 
                          isSecDraggedOver ? 'border-[#00b26f] shadow-lg scale-[1.01] bg-emerald-50/20' : 'border-[#e1e5e9]'
                        }`}
                        draggable
                        onDragStart={(e) => handleSectionDragStart(e, sectionIndex)}
                        onDragOver={(e) => handleSectionDragOver(e, sectionIndex)}
                        onDrop={(e) => handleSectionDrop(e, sectionIndex)}
                        onDragEnd={handleSectionDragEnd}
                      >
                        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center mb-4 gap-3">
                          <div className="flex items-center gap-2 flex-1">
                            <div 
                              className="section-drag-handle cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 px-1.5 py-2 flex items-center justify-center"
                              title="Drag to reorder section"
                            >
                              <i className="fa-solid fa-grip-vertical text-lg"></i>
                            </div>
                            <div className="relative flex-1 group">
                              <i className="fa-solid fa-folder-open absolute left-3 top-1/2 -translate-y-1/2 text-[#00b26f] opacity-50"></i>
                              <input
                                type="text"
                                value={section.title}
                                onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 md:py-3 border-2 border-[#e1e5e9] rounded-xl text-sm md:text-base font-bold text-[#333] transition-all duration-300 focus:outline-none focus:border-[#00b26f] bg-white group-hover:border-emerald-200"
                                placeholder="Section Title"
                              />
                            </div>
                          </div>
                        <div className="flex gap-2 h-[42px] md:h-[48px]">
                          <button
                            type="button"
                            onClick={() => openLectureModal(sectionIndex)}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 md:px-5 bg-[#00b26f] text-white rounded-xl hover:bg-[#008f5a] transition-all duration-300 text-xs md:text-sm font-bold shadow-sm"
                          >
                            <i className="fa-solid fa-plus-circle"></i>
                            Add Lecture
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSection(sectionIndex)}
                            className="w-10 md:w-12 flex items-center justify-center text-[#dc3545] bg-white border-2 border-[#e1e5e9] rounded-xl hover:bg-[#dc3545] hover:text-white hover:border-[#dc3545] transition-all duration-300"
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 mt-4">
                        {section.lectures.map((lecture, lectureIndex) => {
                          const isCurrentlyUploading = lecture.uploadProgress !== undefined && lecture.uploadProgress !== null;
                          const isLecDragged = draggedLecture && draggedLecture.sectionIndex === sectionIndex && draggedLecture.lectureIndex === lectureIndex;
                          const isLecDraggedOver = draggedOverLecture && draggedOverLecture.sectionIndex === sectionIndex && draggedOverLecture.lectureIndex === lectureIndex && !isLecDragged;
                          return (
                            <div 
                              key={lectureIndex} 
                              draggable
                              onDragStart={(e) => handleLectureDragStart(e, sectionIndex, lectureIndex)}
                              onDragOver={(e) => handleLectureDragOver(e, sectionIndex, lectureIndex)}
                              onDrop={(e) => handleLectureDrop(e, sectionIndex, lectureIndex)}
                              onDragEnd={handleLectureDragEnd}
                              className={`bg-white p-3 md:p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border transition-all duration-200 cursor-move ${
                                isLecDragged ? 'opacity-30 border-dashed border-[#00b26f] bg-emerald-50/10' : 
                                isLecDraggedOver ? 'border-[#00b26f] shadow-md scale-[1.01] bg-emerald-50/20' : 'border-[#e1e5e9] hover:shadow-md'
                              }`}
                            >
                              <div className="flex items-start gap-3 w-full">
                                <div 
                                  className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 self-center px-1"
                                  title="Drag to reorder lecture"
                                >
                                  <i className="fa-solid fa-grip-vertical"></i>
                                </div>
                                <div className="w-8 h-8 md:w-10 md:h-10 bg-[#00b26f] text-white rounded-lg flex-shrink-0 flex items-center justify-center">
                                  <i className={`fa-solid ${lecture.type === 'video' ? 'fa-play' :
                                    lecture.type === 'article' ? 'fa-file-alt' :
                                      lecture.type === 'quiz' ? 'fa-question-circle' :
                                        'fa-clipboard'
                                    } text-sm md:text-base`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm md:text-[0.95rem] mb-1 font-bold text-[#333] break-words whitespace-normal leading-tight">{lecture.title}</h4>
                                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] md:text-[0.85rem] text-[#666]">
                                    <span className="flex items-center gap-1"><i className="fa-regular fa-clock"></i> {lecture.duration}</span>
                                    <span className="text-[#e1e5e9]">|</span>
                                    <span className="capitalize">{lecture.type}</span>
                                    {lecture.preview && (
                                      <>
                                        <span className="text-[#e1e5e9]">|</span>
                                        <span className="text-[#00b26f] font-bold">Preview</span>
                                      </>
                                    )}
                                  </div>

                                  {isCurrentlyUploading && (
                                    <div className="mt-2.5 max-w-md">
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs font-semibold text-[#00b26f] flex items-center gap-1">
                                          <i className="fa-solid fa-spinner animate-spin"></i> Uploading video...
                                        </span>
                                        <span className="text-xs font-bold text-[#00b26f]">{lecture.uploadProgress}%</span>
                                      </div>
                                      <div className="w-full bg-[#e1e5e9] rounded-full h-2 overflow-hidden">
                                        <div 
                                          className="bg-[#00b26f] h-2 rounded-full transition-all duration-300 ease-out" 
                                          style={{ width: `${lecture.uploadProgress}%` }}
                                        ></div>
                                      </div>
                                      {lecture.uploadInfo && (
                                        <div className="flex justify-between items-center mt-1 text-[10px] text-[#666] font-medium">
                                          <span>
                                            {(lecture.uploadInfo.loaded / (1024 * 1024)).toFixed(1)} MB / {(lecture.uploadInfo.total / (1024 * 1024)).toFixed(1)} MB
                                          </span>
                                          <span className="flex items-center gap-1.5">
                                            <span className="text-[#dc3545] font-semibold">
                                              {(((lecture.uploadInfo.total - lecture.uploadInfo.loaded) / (1024 * 1024))).toFixed(1)} MB left
                                            </span>
                                            {lecture.uploadInfo.speed > 0 && (
                                              <>
                                                <span className="text-[#e1e5e9]">|</span>
                                                <span>{(lecture.uploadInfo.speed / (1024 * 1024)).toFixed(1)} MB/s</span>
                                              </>
                                            )}
                                            {lecture.uploadInfo.eta > 0 && (
                                              <>
                                                <span className="text-[#e1e5e9]">|</span>
                                                <span>{lecture.uploadInfo.eta > 60 ? `${Math.floor(lecture.uploadInfo.eta / 60)}m ${Math.round(lecture.uploadInfo.eta % 60)}s` : `${Math.round(lecture.uploadInfo.eta)}s`} left</span>
                                              </>
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 w-full sm:w-auto justify-end pt-2 sm:pt-0 border-t sm:border-0 border-[#f0f2f4]">
                                {lecture.type === 'video' && (lecture.videoUrl || lecture.video_url) && 
                                 (lecture.videoUrl || lecture.video_url) !== 'Uploading...' && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const rawUrl = lecture.videoUrl || lecture.video_url || '';
                                      const fullUrl = getMediaUrl(rawUrl);
                                      setVideoPreview({ title: lecture.title, url: fullUrl });
                                    }}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1.5 text-[#6f42c1] bg-[rgba(111,66,193,0.1)] rounded-md hover:bg-[#6f42c1] hover:text-white transition-all duration-300 text-xs font-bold"
                                  >
                                    <i className="fa-solid fa-eye"></i>
                                    <span>Preview</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  disabled={isCurrentlyUploading}
                                  onClick={() => openLectureModal(sectionIndex, lecture)}
                                  className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1.5 text-[#00b26f] bg-[rgba(0,178,111,0.1)] rounded-md hover:bg-[#00b26f] hover:text-white transition-all duration-300 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fa-solid fa-edit"></i>
                                  <span>Edit</span>
                                </button>
                                <button
                                  type="button"
                                  disabled={isCurrentlyUploading}
                                  onClick={() => removeLecture(sectionIndex, lectureIndex)}
                                  className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1.5 text-[#dc3545] bg-[rgba(220,53,69,0.1)] rounded-md hover:bg-[#dc3545] hover:text-white transition-all duration-300 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fa-solid fa-times"></i>
                                  <span>Remove</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        {section.lectures.length === 0 && (
                          <div className="text-center text-[#666] py-6 bg-white rounded-lg border border-dashed border-[#e1e5e9]">
                            <i className="fa-solid fa-video text-3xl mb-2 text-[#e1e5e9]"></i>
                            <p className="text-sm">No lectures yet. Click "Add Lecture" to get started.</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}

                  {sections.length === 0 && (
                    <div className="text-center text-[#666] py-12 bg-white rounded-xl border-2 border-dashed border-[#e1e5e9]">
                      <i className="fa-solid fa-list text-5xl mb-4 text-[#e1e5e9]"></i>
                      <p className="text-lg mb-2">No sections added yet</p>
                      <p className="text-sm mb-4">Click "Add Section" to start building your curriculum</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Study Materials Section */}
            {activeSection === 'study-materials' && (
              <section className="bg-white rounded-xl p-5 md:p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-6 md:mb-8 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-xl md:text-2xl font-bold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-folder-open text-[#00b26f]"></i> Study Materials & Downloads
                  </h2>
                  <p className="text-[#666] text-xs md:text-sm mt-1">
                    Upload PDFs and Photos/Images that will unlock for students upon course purchase.
                  </p>
                </div>

                {/* Upload Form Card */}
                <div className="bg-[#f4f6f8] p-5 md:p-6 rounded-xl mb-8 border border-[#e1e5e9]">
                  <h3 className="text-base font-bold text-[#333] mb-4 flex items-center gap-2">
                    <i className="fa-solid fa-cloud-arrow-up text-[#00b26f]"></i> Add New Study Material
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block font-semibold mb-1.5 text-[#333] text-sm">Material Title</label>
                      <input
                        type="text"
                        value={newMaterialTitle}
                        onChange={(e) => setNewMaterialTitle(e.target.value)}
                        placeholder="e.g., Lecture 1 Notes, Architecture Diagram"
                        className="w-full px-4 py-2.5 border-2 border-[#e1e5e9] rounded-lg text-sm transition-all focus:outline-none focus:border-[#00b26f] bg-white"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold mb-1.5 text-[#333] text-sm">Material Type</label>
                      <select
                        value={newMaterialType}
                        onChange={(e) => setNewMaterialType(e.target.value)}
                        className="w-full px-4 py-2.5 border-2 border-[#e1e5e9] rounded-lg text-sm transition-all focus:outline-none focus:border-[#00b26f] bg-white"
                      >
                        <option value="pdf">📄 PDF Document (.pdf)</option>
                        <option value="image">🖼️ Photo / Image (.jpg, .png, .webp)</option>
                      </select>
                    </div>
                  </div>

                  {/* Dropzone Upload Button */}
                  <div className="mt-2">
                    <button
                      type="button"
                      disabled={materialUploading}
                      onClick={() => materialFileInputRef.current?.click()}
                      className="w-full py-6 border-2 border-dashed border-[#00b26f] rounded-xl flex flex-col items-center justify-center gap-2 bg-emerald-50/30 hover:bg-emerald-50/70 transition-all duration-300 group cursor-pointer disabled:opacity-50"
                    >
                      <div className="w-12 h-12 rounded-full bg-[#00b26f]/10 text-[#00b26f] flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                        {materialUploading ? (
                          <i className="fa-solid fa-spinner animate-spin"></i>
                        ) : newMaterialType === 'pdf' ? (
                          <i className="fa-solid fa-file-pdf"></i>
                        ) : (
                          <i className="fa-solid fa-image"></i>
                        )}
                      </div>
                      <span className="font-bold text-[#333] text-sm">
                        {materialUploading ? 'Uploading file...' : 'Click to Upload File (PDF or Photo)'}
                      </span>
                      <span className="text-xs text-[#666]">Supported formats: .pdf, .png, .jpg, .jpeg, .webp</span>
                    </button>

                    <input
                      ref={materialFileInputRef}
                      type="file"
                      accept={newMaterialType === 'pdf' ? '.pdf,application/pdf' : 'image/*'}
                      onChange={handleMaterialFileUpload}
                      className="hidden"
                    />
                  </div>

                  {materialUploading && (
                    <div className="mt-4 max-w-md mx-auto">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold text-[#00b26f] flex items-center gap-1">
                          <i className="fa-solid fa-spinner animate-spin"></i> Uploading...
                        </span>
                        <span className="text-xs font-bold text-[#00b26f]">{materialUploadProgress}%</span>
                      </div>
                      <div className="w-full bg-[#e1e5e9] rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-[#00b26f] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${materialUploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Uploaded Materials List */}
                <div>
                  <h3 className="text-base font-bold text-[#333] mb-4 flex items-center gap-2">
                    <i className="fa-solid fa-list text-[#00b26f]"></i> Added Study Materials ({studyMaterials.length})
                  </h3>

                  {studyMaterials.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {studyMaterials.map((material, index) => {
                        const fileUrl = material.fileUrl || material.file_url || '';
                        const isPdf = (material.fileType || material.file_type) === 'pdf';
                        const fullUrl = getMediaUrl(fileUrl);

                        return (
                          <div
                            key={index}
                            className="bg-white p-4 rounded-xl border border-[#e1e5e9] shadow-sm hover:shadow-md transition-all flex items-center justify-between gap-4"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-xl font-bold ${
                                isPdf ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-500'
                              }`}>
                                {isPdf ? (
                                  <i className="fa-solid fa-file-pdf"></i>
                                ) : (
                                  <i className="fa-solid fa-image"></i>
                                )}
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-bold text-sm text-[#333] truncate">{material.title || 'Untitled Material'}</h4>
                                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded uppercase mt-1 ${
                                  isPdf ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {isPdf ? 'PDF Document' : 'Photo / Image'}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-9 h-9 bg-emerald-50 text-[#00b26f] hover:bg-[#00b26f] hover:text-white rounded-lg flex items-center justify-center transition-all text-xs font-bold"
                                title="Preview File"
                              >
                                <i className="fa-solid fa-arrow-up-right-from-square"></i>
                              </a>
                              <button
                                type="button"
                                onClick={() => handleRemoveMaterial(index)}
                                className="w-9 h-9 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg flex items-center justify-center transition-all text-xs font-bold"
                                title="Remove Material"
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-[#f4f6f8] rounded-xl border-2 border-dashed border-[#e1e5e9]">
                      <i className="fa-solid fa-folder-open text-4xl text-gray-300 mb-3 block"></i>
                      <p className="text-gray-500 text-sm font-medium">No study materials added yet.</p>
                      <p className="text-gray-400 text-xs mt-1">Upload PDFs or Photos above to provide study materials for this course.</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Requirements Section */}
            {activeSection === 'requirements' && (

              <section className="bg-white rounded-xl p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-8 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-2xl font-semibold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-clipboard-list"></i> Requirements & Prerequisites
                  </h2>
                  <p className="text-[#666] text-sm mt-2">What students need before taking this course</p>
                </div>

                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-[#333] mb-4">Requirements</h3>
                  <ListManager
                    items={requirements}
                    onAdd={addRequirement}
                    onRemove={removeRequirement}
                    placeholder="e.g., Basic understanding of Python"
                    addButtonText="Add Requirement"
                  />
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#333] mb-4">Skills Students Will Gain</h3>
                  <div className="bg-[#f4f6f8] p-4 md:p-6 rounded-xl">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {skills.map((skill, index) => (
                        <div key={index} className="bg-gradient-to-br from-[#00b26f] to-[#008f5a] text-white px-3 md:px-4 py-2 md:py-3 rounded-lg text-center flex items-center justify-between">
                          <span className="flex-1 truncate">{skill}</span>
                          <button
                            type="button"
                            onClick={() => removeSkill(index)}
                            className="text-lg ml-2 hover:bg-[rgba(255,255,255,0.2)] rounded transition-all duration-300"
                          >
                            <i className="fa-solid fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        id="skillInput"
                        className="flex-1 px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                        placeholder="e.g., Python, JavaScript, React"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addSkill(e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.getElementById('skillInput');
                          addSkill(input.value);
                          input.value = '';
                        }}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#00b26f] text-white rounded-lg font-semibold hover:bg-[#008f5a] transition-all duration-300"
                      >
                        <i className="fa-solid fa-plus"></i> Add
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* What You'll Learn Section */}
            {activeSection === 'objectives' && (
              <section className="bg-white rounded-xl p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-8 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-2xl font-semibold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-bullseye"></i> What Students Will Learn
                  </h2>
                  <p className="text-[#666] text-sm mt-2">List the key learning outcomes and objectives</p>
                </div>

                <ListManager
                  items={whatYouLearn}
                  onAdd={addWhatYouLearn}
                  onRemove={removeWhatYouLearn}
                  placeholder="e.g., Build professional web applications from scratch"
                  addButtonText="Add Learning Objective"
                />
              </section>
            )}

            {/* Full Description Section */}
            {activeSection === 'description' && (
              <section className="bg-white rounded-xl p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-8 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-2xl font-semibold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-file-alt"></i> Full Description
                  </h2>
                  <p className="text-[#666] text-sm mt-2">Provide a detailed description of your course content and what students will learn</p>
                </div>

                {/* Rich Text Editor Toolbar */}
                <div className="mb-4 bg-[#f4f6f8] p-4 rounded-xl">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => execCommand('bold')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Bold"
                    >
                      <i className="fa-solid fa-bold"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('italic')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Italic"
                    >
                      <i className="fa-solid fa-italic"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('underline')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Underline"
                    >
                      <i className="fa-solid fa-underline"></i>
                    </button>
                    <div className="w-px h-8 bg-[#e1e5e9] mx-2"></div>
                    <button
                      type="button"
                      onClick={() => execCommand('insertUnorderedList')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Bullet List"
                    >
                      <i className="fa-solid fa-list-ul"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('insertOrderedList')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Numbered List"
                    >
                      <i className="fa-solid fa-list-ol"></i>
                    </button>
                    <div className="w-px h-8 bg-[#e1e5e9] mx-2"></div>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyLeft')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Align Left"
                    >
                      <i className="fa-solid fa-align-left"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyCenter')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Align Center"
                    >
                      <i className="fa-solid fa-align-center"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyRight')}
                      className="px-3 py-2 bg-white border border-[#e1e5e9] rounded-lg hover:bg-[#00b26f] hover:text-white transition-all duration-300"
                      title="Align Right"
                    >
                      <i className="fa-solid fa-align-right"></i>
                    </button>
                  </div>
                </div>

                {/* Rich Text Editor */}
                <div className="mb-6">
                  <label className="block font-semibold mb-2 text-[#333] text-sm">
                    Course Description <span className="text-[#dc3545]">*</span>
                  </label>
                  <div
                    ref={fullDescriptionRef}
                    contentEditable
                    onInput={() => setIsDirty(true)}
                    className="w-full min-h-[400px] px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)] bg-white"
                    style={{
                      lineHeight: '1.6',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Describe your course in detail. What will students learn? What are the key topics covered? What makes this course unique?"
                  ></div>
                  <small className="text-[#666] text-[0.85rem] mt-2 block">
                    Use the toolbar above to format your text. Include important details about course content, learning outcomes, and prerequisites.
                  </small>
                </div>

                {/* Preview Section */}
                <div className="bg-[#f4f6f8] p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-[#333] mb-4">Description Preview</h3>
                  <div className="bg-white p-4 rounded-lg border border-[#e1e5e9]">
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: fullDescriptionRef.current?.innerHTML || '<p class="text-[#666] italic">No description added yet...</p>'
                      }}
                    ></div>
                  </div>
                </div>
              </section>
            )}

            {/* Instructor Info Section */}
            {activeSection === 'instructor' && (
              <section className="bg-white rounded-xl p-8 mb-8 shadow-md animate-fadeIn">
                <div className="mb-8 pb-4 border-b-2 border-[#e1e5e9]">
                  <h2 className="text-2xl font-semibold text-[#333] flex items-center gap-2">
                    <i className="fa-solid fa-user-tie"></i> Instructor Information
                  </h2>
                  <p className="text-[#666] text-sm mt-2">Details about the course instructor</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">
                      Instructor Name <span className="text-[#dc3545]">*</span>
                    </label>
                    <input
                      type="text"
                      name="instructor.name"
                      value={courseData.instructorDetails.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., Dr. Angela Yu"
                      required
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-[#333] text-sm">Title</label>
                    <input
                      type="text"
                      name="instructor.title"
                      value={courseData.instructorDetails.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                      placeholder="e.g., Developer and Lead Instructor"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block font-semibold mb-2 text-[#333] text-sm">Instructor Bio</label>
                  <textarea
                    name="instructor.bio"
                    value={courseData.instructorDetails.bio}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)] min-h-[120px] resize-y"
                    placeholder="Brief biography of the instructor..."
                  ></textarea>
                </div>

              </section>
            )}
          </form>
        </main>
      </div>

      {/* Lecture Modal */}
      {showLectureModal && (
        <div className="fixed inset-0 z-[1000] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-[600px] max-h-[90vh] overflow-y-auto animate-modalSlideIn">
            <div className="p-6 border-b border-[#e1e5e9] flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-semibold">
                {editingLecture ? 'Edit Lecture' : 'Add New Lecture'}
              </h3>
              <button
                type="button"
                onClick={closeLectureModal}
                className="text-2xl text-[#666] hover:text-[#dc3545] transition-colors"
              >
                &times;
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block font-semibold mb-2 text-[#333] text-sm">
                  Lecture Title <span className="text-[#dc3545]">*</span>
                </label>
                <input
                  type="text"
                  value={lectureData.title}
                  onChange={(e) => setLectureData({ ...lectureData, title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="e.g., Introduction to HTML"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block font-semibold mb-2 text-[#333] text-sm">
                    Duration <span className="text-[#dc3545]">*</span>
                  </label>
                  <input
                    type="text"
                    value={lectureData.duration}
                    onChange={(e) => setLectureData({ ...lectureData, duration: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                    placeholder="e.g., 15:30"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 text-[#333] text-sm">
                    Type <span className="text-[#dc3545]">*</span>
                  </label>
                  <select
                    value={lectureData.type}
                    onChange={(e) => setLectureData({ ...lectureData, type: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  >
                    <option value="video">Video</option>
                    <option value="article">Article</option>
                    <option value="quiz">Quiz</option>
                    <option value="assignment">Assignment</option>
                  </select>
                </div>
              </div>

              {lectureData.type === 'video' && (
                <div className="mb-4">
                  <label className="block font-semibold mb-2 text-[#333] text-sm">
                    Video URL
                  </label>
                  <input
                    type="text"
                    value={lectureData.videoUrl || lectureData.video_url || ''}
                    onChange={(e) => setLectureData({ ...lectureData, videoUrl: e.target.value, video_url: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                    placeholder="Enter external Video URL (e.g. YouTube/Vimeo) or filename"
                  />
                </div>
              )}



              <div className="mb-4">
                <label className="block font-semibold mb-2 text-[#333] text-sm">Upload Local File (Sets Filename)</label>
                <input
                  type="file"
                  accept={lectureData.type === 'video' ? 'video/*' : '*/*'}
                  className="w-full px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setLectureData(prev => ({
                        ...prev,
                        videoUrl: 'Uploading...',
                        fileToUpload: file
                      }));

                      // Auto-calculate video duration
                      if (file.type.startsWith('video/')) {
                        const tempVideo = document.createElement('video');
                        tempVideo.preload = 'metadata';
                        tempVideo.onloadedmetadata = () => {
                          URL.revokeObjectURL(tempVideo.src);
                          const seconds = tempVideo.duration;
                          const hrs = Math.floor(seconds / 3600);
                          const mins = Math.floor((seconds % 3600) / 60);
                          const secs = Math.floor(seconds % 60);
                          let formattedDuration = '';
                          if (hrs > 0) {
                            formattedDuration = `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                          } else {
                            formattedDuration = `${mins}:${secs.toString().padStart(2, '0')}`;
                          }
                          setLectureData(prev => ({ ...prev, duration: formattedDuration }));
                        };
                        tempVideo.src = URL.createObjectURL(file);
                      }
                    }
                  }}
                />
                <small className="text-[#666] text-sm mt-1 block">
                  Selecting a file will set the Video URL to the filename. Ensure the file is in <code>backend/uploads/</code>.
                </small>
                
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lectureData.preview}
                    onChange={(e) => setLectureData({ ...lectureData, preview: e.target.checked })}
                    className="w-4 h-4 text-[#00b26f]"
                  />
                  <span className="text-sm text-[#333]">Allow free preview of this lecture</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-[#e1e5e9] flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={closeLectureModal}
                className="px-5 py-2.5 bg-[#666] text-white rounded-lg font-semibold hover:bg-[#555] transition-all duration-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveLecture}
                disabled={!lectureData.title || !lectureData.duration}
                className="px-5 py-2.5 bg-[#00b26f] text-white rounded-lg font-semibold hover:bg-[#008f5a] transition-all duration-300 disabled:bg-[#e1e5e9] disabled:cursor-not-allowed"
              >
                {editingLecture ? 'Update Lecture' : 'Add Lecture'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Global Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] flex justify-center items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-modalSlideIn">
            <div className="p-8 text-center">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${confirmModal.type === 'danger' ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-500'}`}>
                <i className={`fa-solid ${confirmModal.type === 'danger' ? 'fa-triangle-exclamation' : 'fa-check-double'} text-3xl`}></i>
              </div>
              <h3 className="text-2xl font-bold text-[#333] mb-2">{confirmModal.title}</h3>
              <p className="text-[#666] mb-8">{confirmModal.message}</p>
              <div className="flex gap-4">
                {!confirmModal.isAlert && (
                  <button
                    onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                    className="flex-1 px-6 py-3 bg-[#f4f6f8] text-[#666] font-bold rounded-xl hover:bg-[#e1e5e9] transition-all"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={confirmModal.onConfirm || (() => setConfirmModal({ ...confirmModal, show: false }))}
                  className={`flex-1 px-6 py-3 text-white font-bold rounded-xl shadow-lg transition-all ${confirmModal.type === 'danger' ? 'bg-red-500 hover:bg-red-600 shadow-red-500/30' : 'bg-[#00b26f] hover:bg-[#008f5a] shadow-[#00b26f]/30'}`}
                >
                  {confirmModal.confirmText || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {videoPreview && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex justify-center items-center p-4"
          onClick={() => setVideoPreview(null)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl animate-modalSlideIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#333]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#00b26f] rounded-lg flex items-center justify-center">
                  <i className="fa-solid fa-play text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{videoPreview.title}</h3>
                  <p className="text-gray-400 text-xs">Video Preview</p>
                </div>
              </div>
              <button
                onClick={() => setVideoPreview(null)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-300"
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {/* Video Player */}
            <div className="aspect-video bg-black relative">
              {videoPreview.url.includes('youtube.com') || videoPreview.url.includes('youtu.be') ? (
                <iframe
                  className="w-full h-full"
                  src={videoPreview.url.replace('watch?v=', 'embed/')}
                  title={videoPreview.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  key={videoPreview.url}
                  controls
                  autoPlay
                  className="w-full h-full"
                  src={getMediaUrl(videoPreview.url)}
                  onError={(e) => {
                    console.error('Video failed to load:', videoPreview.url);
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:#999;text-align:center;padding:2rem">
                        <i class="fa-solid fa-triangle-exclamation" style="font-size:3rem;color:#dc3545;margin-bottom:1rem"></i>
                        <p style="font-size:1.1rem;font-weight:bold;color:#fff;margin-bottom:0.5rem">Video failed to load</p>
                        <p style="font-size:0.85rem;color:#888;word-break:break-all;max-width:90%">${videoPreview.url}</p>
                        <p style="font-size:0.75rem;color:#666;margin-top:1rem">Check that the video file exists on the server</p>
                      </div>
                    `;
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx="true">{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.95) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modalSlideIn {
          animation: modalSlideIn 0.3s ease-out forwards;
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
        }
      `}</style>

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-[2000] animate-slideInRight">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border-l-4 min-w-[300px] ${toast.type === 'success'
              ? 'bg-white border-[#00b26f]'
              : 'bg-white border-[#dc3545]'
            }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === 'success' ? 'bg-[#e6f7f0] text-[#00b26f]' : 'bg-[#fdf2f2] text-[#dc3545]'
              }`}>
              <i className={`fa-solid ${toast.type === 'success' ? 'fa-check-circle' : 'fa-circle-exclamation'} text-lg`}></i>
            </div>
            <div className="flex-1">
              <h4 className={`text-sm font-bold ${toast.type === 'success' ? 'text-[#00b26f]' : 'text-[#dc3545]'}`}>
                {toast.type === 'success' ? 'Success' : 'Error'}
              </h4>
              <p className="text-[#333] text-xs font-medium">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ ...toast, show: false })}
              className="text-[#999] hover:text-[#333] transition-colors"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// List Manager Component
const ListManager = ({ items, onAdd, onRemove, placeholder, addButtonText }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="bg-[#f4f6f8] p-6 rounded-xl">
      <div className="flex flex-col gap-3 mb-4">
        {items.map((item, index) => (
          <div key={index} className="bg-white p-4 rounded-lg flex justify-between items-center border border-[#e1e5e9] hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-6 h-6 bg-[#00b26f] text-white rounded-full flex items-center justify-center text-sm font-semibold">
                {index + 1}
              </div>
              <span className="flex-1">{item}</span>
            </div>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="text-[#dc3545] text-xl p-2 rounded-md hover:bg-[#dc3545] hover:text-white transition-all duration-300"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        ))}

        {items.length === 0 && (
          <div className="text-center text-[#666] py-6">
            <p className="text-sm">No items added yet</p>
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-4 py-3 border-2 border-[#e1e5e9] rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#00b26f] text-white rounded-lg font-semibold hover:bg-[#008f5a] transition-all duration-300 whitespace-nowrap"
        >
          <i className="fa-solid fa-plus"></i> {addButtonText}
        </button>
      </div>
    </div>
  );
};

export default ManageCourse;
